/**
 * 订单模块接口
 * @description 下单、退票、异常订单、车票等订单相关接口
 */

import axios from "@/utils/request";
import Api from "../api";
import * as Request from "../request.type";
import * as ResponseData from "../response.type";

/**
 * 该函数用于通过 HTTP POST 请求向服务器发送座位详细信息。
 */
export const Http_postSeatDetail = async (
  params: Request.postSeatDetailParams
) => {
  return await axios.post<ResponseData.ResponseSeatDetail>(
    Api.POST_SEAT_DETAIL,
    params
  );
};

/** 普通车票消位 */
export const Http_postCommonTicketToOpen = async (
  params: Request.postCommonTicketToOpenParams
) => {
  return await axios.post(Api.POST_COMMON_TICKET_TO_OPEN, params);
};

/** 普通车票消位预检查 */
export const Http_postCommonTicketToOpenPre = async (
  params: Request.postCommonTicketToOpenParams
) => {
  return await axios.post(Api.POST_COMMON_TICKET_TO_OPEN_PRE, params);
};

/**
 * 获取打印收据url地址
 * @returns 获取打印收据
 */
export const Http_getReceipt = async (
  /** 订单ID */
  id: number
) => {
  return await axios.get<ResponseData.Receipt>(Api.GET_RECEIPT, {
    params: {
      id,
    },
  });
};

/**
 * 核心系统，代理买断电子票展示车票号
 * @returns 获取车票号列表
 */
export const Http_getStockTicketDetails = async (
  /** 订单ID */
  params: Request.GetStockTicketDetailsParams
) => {
  return await axios.get<number[]>(Api.GET_STOCK_TICKET_DETAILS, {
    params,
  });
};

/**
 * 包车票后台退票预校验
 */
export const Http_postRefundVerify = async (charteredTicketNo: string) => {
  return await axios.post<ResponseData.RefundVerifyItem>(
    Api.POST_REFUND_PRE_VERIFY,
    {
      charteredTicketNo,
    }
  );
};

/**
 * 包车票后台退票确认
 */
export const Http_postRefundConfirm = async (
  params: Request.RefundConfirmParams
) => {
  return await axios.post<ResponseData.RefundConfirmItem>(
    Api.POST_REFUND_CONFIRM,
    params
  );
};

/**
 * 行李票后台退票预校验
 */
export const Http_postBaggageRefundPreVerify = async (
  baggageTicketNo: string
) => {
  return await axios.post<ResponseData.BaggageRefundVerify>(
    Api.POST_BAGGAGE_REFUND_PRE_VERIFY,
    {
      baggageTicketNo,
    }
  );
};

/**
 * 行李票后台退票确认
 */
export const Http_postBaggageRefundConfirm = async (
  params: Request.BaggageRefundConfirmParams
) => {
  return await axios.post<ResponseData.BaggageRefundConfirm>(
    Api.POST_BAGGAGE_REFUND_CONFIRM,
    params
  );
};

/**
 * 补价票下单
 */
export const Http_postPremiumTicketOrder = async (params: {
  /** 是否常规售卖补价票，0-否，1-是，默认是 */
  isNormalTakeOrder?: 0 | 1;
  takeTicketReqs: {
    /** 绑定的车票ID */
    ticketId: number;
    /** 补价票金额（目前只有通融改签或者取位补差价才会有值，其他的都是直接查ticket表对应的补价金额） */
    premiumTicketAmount?: number;
  }[];
}) => {
  return await axios.post<any>(Api.POST_PREMIUM_TICKET_ORDER, params);
};

/**
 * 补价票单出
 * @author Alfie
 * @description 用于车票查询页面的补价功能，支持指定补价金额
 * @param params 补价票单出参数
 * @returns 订单信息
 */
export const Http_postSingleOutPremiumTicketOrder = async (params: {
  takeTicketReqs: {
    /** 车票ID */
    ticketId: number;
    /** 补价金额 */
    premiumTicketAmount: number;
  }[];
}) => {
  return await axios.post<any>(
    Api.POST_SINGLE_OUT_PREMIUM_TICKET_ORDER,
    params
  );
};

/**
 * 查询异常订单
 */
export const Http_getOrderAbnormal = async () => {
  return await axios.get<ResponseData.AbnormalOrderInfo[]>(
    Api.GET_ORDER_ABNORMAL_QUERY
  );
};

/**
 * 取消异常订单
 */
export const Http_postOrderAbnormalCancel = async (params: {
  orderId: string;
}) => {
  return await axios.post(Api.POST_ORDER_ABNORMAL_QUERY, params);
};

/**
 * Open票下单
 * @param params 下单参数
 * @returns 订单信息
 */
export const Http_postOpenOrderTake = async (
  params: Request.PostOpenOrderTakeParams
) => {
  return await axios.post<ResponseData.OpenOrderTakeData>(
    Api.POST_OPEN_ORDER_TAKE,
    params
  );
};

/**
 * 清空购物车解除锁位
 */
export const Http_postOpenOrderClear = async (params: {
  /** 业务类型 1-门市购物车 3-取位购物车 */
  businessType: string;
}) => {
  return await axios.get<any>(Api.POST_OPEN_ORDER_CLEAR, {
    params,
  });
};

/**
 * 作废现金券
 * @param params 作废现金券参数
 * @returns 作废现金券结果
 */
export const Http_postCashCouponInvalid = async (
  params: Request.PostCashCouponInvalidParams
) => {
  return await axios.post(Api.POST_CASH_COUPON_INVALID, params);
};

/**
 * 获取Open票价格规则（包含标准价和营销价）
 * @param params 查询参数
 * @returns 价格规则列表
 */
export const Http_getOpenPriceWithMarket = async (
  params: Request.GetOpenPriceWithMarketParams
) => {
  return await axios.get<ResponseData.OpenPriceRuleOption[]>(
    Api.GET_OPEN_PRICE_WITH_MARKET,
    {
      params,
    }
  );
};

/**
 * 获取班次票价列表
 * @param params 查询参数
 * @returns 班次票价列表
 */
export const Http_getLinePricesForDisplay = async (
  params: Request.GetLinePricesForDisplayParams
) => {
  return await axios.get<ResponseData.GetLinePricesForDisplayData>(
    Api.GET_LINE_PRICES_FOR_DISPLAY,
    {
      params,
    }
  );
};

/**
 * 票码转票号
 * @param params 票码转票号参数
 * @returns 转换后的票号
 */
export const Http_getTakeTicketCodeToTicketNo = async (
  params: Request.TakeTicketCodeToTicketNoParams
) => {
  return await axios.get(Api.GET_TAKE_TICKET_CODE_TO_TICKET_NO, {
    params,
  });
};

/**
 * 批量查询车票
 * @param params 查询参数
 * @returns 车票列表
 */
export const Http_getBatchOperateQuery = async (
  params: Request.BatchOperateQueryParams
) => {
  return await axios.get<ResponseData.BatchOperateQueryData>(
    Api.GET_BATCH_OPERATE_QUERY,
    { params }
  );
};

/**
 * 批量操作车票
 * @param params 批量操作参数
 * @returns 操作结果
 */
export const Http_postBatchOperate = async (
  params: Request.BatchOperateParams
) => {
  return await axios.post<string>(Api.POST_BATCH_OPERATE, params);
};

/**
 * 后台退票预校验
 * @param params 预校验参数
 * @returns 校验结果
 */
export const Http_postBackendRefundPreQuery = async (
  params: Request.BackendRefundPreQueryParams
) => {
  return await axios.post<ResponseData.BackendRefundPreQueryData>(
    Api.POST_BACKEND_REFUND_PRE_QUERY,
    params,
    {
      headers: {
        closeDefaultMessage: true, // 不使用默认提示
      },
    }
  );
};

/**
 * 后台退票计算
 * @param params 计算参数（手续费类型、手续费比率、计算类型、请求ID）
 * @returns 计算结果（手续费、退款金额等）
 */
export const Http_postBackendRefundCompute = async (
  params: Request.BackendRefundComputeParams
) => {
  return await axios.post<ResponseData.BackendRefundComputeData>(
    Api.POST_BACKEND_REFUND_COMPUTE,
    params
  );
};
/**
 * 退票打印预览
 * @param params 预览参数
 * @returns 打印预览数据
 */
export const Http_getRefundPrintPreview = async (
  params: Request.RefundPrintPreviewParams
) => {
  return await axios.get<ResponseData.RefundPrintPreviewData>(
    Api.GET_REFUND_PRINT_PREVIEW,
    { params }
  );
};

/**
 * 退票打印确认
 * @param params 打印确认参数
 * @returns 打印确认数据
 */
export const Http_getRefundPrintConfirm = async (
  params: Request.RefundPrintConfirmParams
) => {
  return await axios.get<ResponseData.RefundPrintConfirmData>(
    Api.GET_REFUND_PRINT_CONFIRM,
    { params }
  );
};

/**
 * 门市订单详情查询
 */
export const Http_getFacadeOrderDetail = async (orderId: string) => {
  return await axios.get<ResponseData.ConsoleOrderDetailData>(
    Api.GET_FACADE_ORDER_DETAIL,
    {
      params: {
        orderId,
      },
    }
  );
};

/**
 * 高级订单详情查询
 * @param orderId 订单ID
 * @returns 订单详情数据
 */
export const Http_getConsoleOrderDetail = async (orderId: string) => {
  return await axios.get<ResponseData.ConsoleOrderDetailData>(
    Api.GET_CONSOLE_ORDER_DETAIL,
    {
      params: {
        orderId,
      },
    }
  );
};

/**
 * 查询销售单位（门市/代理/机构）
 * @param params 查询参数
 * @returns 销售单位列表
 */
export const Http_getSaleUnitQuery = async (
  params: Request.GetSaleUnitQueryParams
) => {
  return await axios.get<ResponseData.SaleUnitInfo[]>(Api.GET_SALE_UNIT_QUERY, {
    params,
  });
};

/**
 * 获取包车票详情
 * @param id 包车票 ID
 * @returns 包车票详情数据
 */
export const Http_postCharteredTicketDetail = async (id: string) => {
  return await axios.post<ResponseData.CharteredDetailData>(
    Api.POST_CHARTERED_TICKET_DETAIL,
    {
      id,
    }
  );
};

/**
 * 修改包车票打印状态
 * @param orderDetailId 包车票明细ID
 * @param printed 打印状态
 */
export const Http_postCharteredTicketPrinted = async (params: {
  orderDetailId: string;
  printed: number;
  remark: string;
}) => {
  return await axios.post<null>(Api.POST_CHARTERED_TICKET_PRINT_STATUS, params);
};

/**
 * 获取打印预览数据
 */
export const Http_getPrintPreview = async (params: {
  /** 订单ID */
  orderId: string | number;
  /** 车票ID */
  ticketNo?: string;
}) => {
  return await axios.get<ResponseData.TicketPrintData>(
    Api.GET_PRINT_PREVIEW_DATA,
    {
      params,
    }
  );
};

/**
 * 打印确认
 */
export const Http_postPrintConfirm = async (params: {
  /** 订单ID */
  orderId: string | number;
}) => {
  return await axios.post<ResponseData.TicketPrintData>(
    Api.POST_PRINT_CONFIRM,
    params
  );
};

/**
 * 修改订单/车票，车票打印状态
 */
export const Http_postPrintChangeStatus = async (params: {
  /** 订单ID ，若传则修改整个订单*/
  // orderId?: string | number;
  /** 包车票传入订单id*/
  orderDetailId?: number;
  /** 包车票传入修改具体打印状态，需要重置的打印状态，0-未打印，1-已打印*/
  printed?: number;
  /** 车票号 */
  ticketNo?: string;
  /** 车票号，可以传多个 */
  ticketNos?: string[];
  /** 备注 */
  remark: string;
}) => {
  return await axios.post<ResponseData.changePrintResultData>(
    Api.POST_COMMON_TICKET_PRINT_STATUS,
    params
  );
};

/** 返程座位改为Open票 */
export const Http_postFacadeSaleLineSeatChangeToOpen = async (params: {
  /** 返程锁座id */
  backLockSeatId: number;
  /** 去程购物车详情id */
  goDetailId: number;
  /** 返程购物车详情id */
  backDetailId: number;
}) => {
  return await axios.post<any>(
    Api.POST_FACADE_SALE_LINE_SEAT_CHANGE_TO_OPEN,
    params
  );
};

/** 门市下单 */
export const Http_postFacadeSaleOrderTake = async (
  params: Request.PostFacadeSaleOrderTakeParams
) => {
  return await axios.post<ResponseData.FacadeSaleOrderTakeData>(
    Api.POST_FACADE_SALE_ORDER_TAKE,
    params
  );
};
